import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import {
  GROUND_FLOOR_NOT_TABULATED,
  constructionById,
  constructionElementForSurfaceKind,
  constructionsForElement,
  formatUValue,
  type Construction,
} from "@sts/rules-data";
import type { SurveySurface, WallAdjacencyInput } from "../api/types.js";
import { usePortal } from "../app/context.js";
import { useSubmit } from "../app/use-submit.js";
import { degrees } from "../lib/format.js";
import {
  ADJACENCY_TEMPERATURE_SOURCE,
  confirmationByline,
  parseAdjacencyTemperature,
} from "../lib/wall-adjacency.js";
import { Failure } from "./Async.jsx";

/**
 * The wall panel (and the floor panel — the same fields, a different list of
 * answers): length, what is on the other side, what it is made of, and the
 * temperature over there.
 *
 * LENGTH IS DERIVED AND SHOWN, NOT STORED. The heat loss is A × U × ΔT: the
 * area is the figure that matters and the one the scan and the plan actually
 * measure. A wall's length is `gross area ÷ height`, and it is shown here
 * because it is how a designer checks a wall against the drawing — but it is
 * shown as what it is, with the area beside it. Typing a length would mean
 * inventing an area from a height that the scan gives per wall and the plan
 * does not; the plan's own wall tools are where a wall's size is corrected
 * (`PlanEditLayer`), and that correction reaches the heat loss through the
 * conversion merge.
 *
 * WALL TYPE IS THE SAME ANSWER AS 0045, not a second mechanism. External,
 * party and internal are three arms of one stored adjacency, one confirmation,
 * and one protection against a re-scan. A floor gets the floor arms instead:
 * on the ground, suspended, exposed, over a heated room, over an unheated one.
 *
 * THE TEMPERATURE ON THE OTHER SIDE IS ALWAYS THE DESIGNER'S. MCS publishes no
 * figure for a neighbouring home, an unheated space or an adjoining room
 * (docs/research/party-walls-2026-09-11.md), so nothing here defaults one and
 * the API refuses a request without one.
 *
 * THE CONSTRUCTION sets the U-value from a published table, and says which
 * table, row and page. A designer with a measurement types it instead, and
 * that stays theirs.
 */

/**
 * The answers each kind of surface can take.
 *
 * THREE WORDINGS, AND THEY ARE NOT INTERCHANGEABLE.
 *
 * `plain` is what somebody picks from: what is on the other side, in the words
 * they would use standing in the room. `hint` gives an example, because
 * "a cold space" means nothing until it means a garage. `label` is the
 * SURVEYING term, kept for reading an answer back in the tables and on the
 * quick-answer buttons, where the reader is a designer checking a record
 * rather than a surveyor deciding.
 *
 * `means` is why this matters more than wording. Picking the wrong side of a
 * wall changes the heat loss silently, and a party wall and a wall to a garage
 * look identical from inside the room — so the consequence is said the moment
 * the answer is chosen, in watts-free English, instead of appearing as a
 * different number somewhere else later.
 */
const WALL_ANSWERS = [
  {
    adjacency: "external",
    label: "External (outside air)",
    plain: "Outside",
    hint: "Fresh air on the other side",
    means: "Counted in full. This is the usual answer for a wall on the outside of the house.",
    needsTemperature: false,
  },
  {
    adjacency: "party",
    label: "Party wall (adjoining home)",
    plain: "The neighbour's house",
    hint: "A joined house, or the flat next door",
    means:
      "Loses no heat: your neighbour heats their side. The U-value is still recorded, just not charged to this design.",
    needsTemperature: true,
  },
  {
    adjacency: "adjacent-room",
    label: "Internal (another heated room)",
    plain: "Another room in this house",
    hint: "A hall, a landing, the next room along",
    means:
      "Loses no heat, unless this survey counts heat between heated rooms (Design conditions).",
    needsTemperature: true,
  },
  {
    adjacency: "unheated-space",
    label: "Unheated space (garage, store…)",
    plain: "A cold space",
    hint: "A garage, a loft, a shared stairway",
    means: "Counted, at the temperature you give below — less than a wall to the street.",
    needsTemperature: true,
  },
] as const;

const FLOOR_ANSWERS = [
  {
    adjacency: "ground",
    label: "Ground (solid, on the ground)",
    plain: "On the ground",
    hint: "Solid floor, straight onto the earth",
    means: "Counted, by the ground-floor method rather than from a table.",
    needsTemperature: false,
  },
  {
    adjacency: "suspended-floor",
    label: "Suspended (ventilated void below)",
    plain: "A vented gap below",
    hint: "Air bricks, a crawl space under the boards",
    means: "Counted, by the ground-floor method rather than from a table.",
    needsTemperature: false,
  },
  {
    adjacency: "external",
    label: "Exposed (outside air below)",
    plain: "Open air below",
    hint: "Over an archway, or an overhang",
    means: "Counted in full, like a wall facing the street.",
    needsTemperature: false,
  },
  {
    adjacency: "adjacent-room",
    label: "Intermediate (heated room below)",
    plain: "A heated room below",
    hint: "Another room of this same house",
    means:
      "Loses no heat, unless this survey counts heat between heated rooms (Design conditions).",
    needsTemperature: true,
  },
  {
    adjacency: "unheated-space",
    label: "Over an unheated space",
    plain: "A cold space below",
    hint: "A garage, a cellar, an undercroft",
    means: "Counted, at the temperature you give below.",
    needsTemperature: true,
  },
  {
    adjacency: "party",
    label: "Another dwelling below (party floor)",
    plain: "Another home below",
    hint: "The flat downstairs",
    means:
      "Loses no heat: whoever lives there heats their side. The U-value is still recorded, just not charged.",
    needsTemperature: true,
  },
] as const;

type Answer = (typeof WALL_ANSWERS)[number] | (typeof FLOOR_ANSWERS)[number];

export function answersFor(kind: string): readonly Answer[] {
  if (kind === "wall") return WALL_ANSWERS;
  if (kind === "floor") return FLOOR_ANSWERS;
  // A window or a door takes the answer of the wall it sits in; a roof or a
  // ceiling is not asked. Returning an empty list hides the control rather
  // than offering a choice the API would refuse.
  return [];
}

/** How each stored adjacency reads back, including ones no panel offers. */
export function adjacencyLabel(adjacency: string, kind: string): string {
  const found = answersFor(kind).find((a) => a.adjacency === adjacency);
  if (found) return found.label;
  if (adjacency === "external") return "External (outside air)";
  if (adjacency === "ground") return "Ground (solid, on the ground)";
  if (adjacency === "suspended-floor") return "Suspended floor";
  if (adjacency === "party") return "Party wall (adjoining home)";
  if (adjacency === "adjacent-room") return "Another heated room";
  if (adjacency === "unheated-space") return "Unheated space";
  return adjacency.replace(/-/g, " ");
}

/** What kind of door this is, from the wall it sits in. */
export function doorKindLabel(adjacency: string): string {
  switch (adjacency) {
    case "external":
      return "External door";
    case "adjacent-room":
      return "Internal door";
    case "unheated-space":
      return "Door to an unheated space";
    case "party":
      return "Door to another home";
    default:
      return "Door";
  }
}

/**
 * A wall's length from its area and its height, when the plan knows both.
 *
 * Returns null rather than a number whenever either is missing or zero: a
 * length of "0.0 m" on a wall whose height nobody captured reads as a
 * measurement, and it is not one.
 */
export function wallLengthM(
  grossAreaM2: number | undefined,
  heightM: number | undefined,
): number | null {
  if (grossAreaM2 === undefined || heightM === undefined) return null;
  if (!Number.isFinite(grossAreaM2) || !Number.isFinite(heightM)) return null;
  if (grossAreaM2 <= 0 || heightM <= 0) return null;
  return grossAreaM2 / heightM;
}

export function SurfacePanel({
  surface,
  roomName,
  canEdit,
  onSaved,
  lengthM,
  heightM,
  internalNotCounted = false,
}: {
  readonly surface: SurveySurface;
  readonly roomName: string;
  readonly canEdit: boolean;
  readonly onSaved: () => void;
  /** From the plan, when it has this wall. Shown, never saved. */
  readonly lengthM?: number | null;
  readonly heightM?: number | null;
  /** The survey takes internal walls and doors at U = 0 (Design conditions, 0057). */
  readonly internalNotCounted?: boolean;
}): ReactNode {
  const { client } = usePortal();
  const adjacencySubmit = useSubmit();
  const constructionSubmit = useSubmit();
  const answers = answersFor(surface.kind);
  const answerId = useId();
  const tempId = useId();
  const constructionId = useId();
  const uValueId = useId();

  const [answer, setAnswer] = useState<string>(surface.adjacency);
  const [temp, setTemp] = useState(
    surface.adjacencyTempC === null ? "" : String(surface.adjacencyTempC),
  );
  const [chosen, setChosen] = useState<string>(surface.constructionId ?? "");
  const [typedU, setTypedU] = useState("");
  const [touched, setTouched] = useState(false);

  // A different surface selected on the plan is a different set of values.
  useEffect(() => {
    setAnswer(surface.adjacency);
    setTemp(surface.adjacencyTempC === null ? "" : String(surface.adjacencyTempC));
    setChosen(surface.constructionId ?? "");
    setTypedU("");
    setTouched(false);
  }, [surface.id, surface.adjacency, surface.adjacencyTempC, surface.constructionId]);

  const element = constructionElementForSurfaceKind(surface.kind);
  /**
   * A floor ON THE GROUND gets no library list at all.
   *
   * The floor entries are RdSAP's Table S12, which is EXPOSED and
   * SEMI-EXPOSED UPPER floors — over outside air, over another heated room,
   * over an unheated space. A floor in contact with the ground, solid or
   * suspended, is calculated instead (§S5.5, BS EN ISO 13370), so there is no
   * row for it; offering the S12 list would let a designer put an upper
   * floor's U-value on a ground floor, which is a worse failure than having
   * no list. The note beside it says so, and the typed box below still works.
   */
  const groundFloor =
    surface.kind === "floor" &&
    (surface.adjacency === "ground" || surface.adjacency === "suspended-floor");
  const library = useMemo<readonly Construction[]>(
    () => (element === null || groundFloor ? [] : constructionsForElement(element)),
    [element, groundFloor],
  );
  const picked = chosen === "" ? undefined : constructionById(chosen);
  /** The answer currently chosen, which three things below need to read. */
  const chosenAnswer = answers.find((a) => a.adjacency === answer);
  const needsTemperature = chosenAnswer?.needsTemperature === true;
  const parsedTemp = parseAdjacencyTemperature(temp);

  const saveAdjacency = (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    const body = needsTemperature
      ? "value" in parsedTemp
        ? ({ adjacency: answer, temperatureC: parsedTemp.value } as WallAdjacencyInput)
        : null
      : ({ adjacency: answer } as WallAdjacencyInput);
    if (body === null) return;
    void adjacencySubmit.submit(async () => {
      await client.setWallAdjacency(surface.id, body);
      onSaved();
    });
  };

  const parsedU = typedU.trim() === "" ? null : Number(typedU.trim());
  const uInvalid = parsedU !== null && (!Number.isFinite(parsedU) || parsedU <= 0 || parsedU > 10);

  return (
    <section
      className="surface-panel"
      data-testid="surface-panel"
      aria-label={`${surface.name ?? surface.kind} in ${roomName}`}
    >
      <h4 className="surface-panel__title">{surface.name ?? surface.kind}</h4>
      {surface.kind === "door" ? (
        <p className="small" data-testid="surface-door-kind">
          <b>{doorKindLabel(surface.adjacency)}</b>
        </p>
      ) : null}
      {surface.adjacency === "party" ? (
        <p className="notice small" data-testid="surface-party-not-counted">
          U = 0 in the heat loss: the other side is another home, heated by whoever lives there. The U-value below
          is recorded for the survey, not charged to this design. A wall or door to an unheated space — a communal
          stair, a garage, a loft — is a different answer, and is still counted.
        </p>
      ) : null}
      {internalNotCounted &&
      surface.adjacency === "adjacent-room" &&
      (surface.kind === "wall" || surface.kind === "door" || surface.kind === "window") ? (
        <p className="notice small" data-testid="surface-not-counted">
          U = 0 in the heat loss: this survey does not count heat between heated rooms (Design conditions). The
          U-value below is used only if that is switched on.
        </p>
      ) : null}

      <dl className="surface-panel__figures">
        {surface.kind === "wall" ? (
          <>
            <dt>Length</dt>
            <dd data-testid="surface-length">
              {lengthM === null || lengthM === undefined ? (
                <span className="muted">not on the plan</span>
              ) : (
                <>
                  {lengthM.toFixed(2)} m{" "}
                  <span className="muted small">
                    from the plan{heightM ? `, at ${heightM.toFixed(2)} m high` : ""}
                  </span>
                </>
              )}
            </dd>
          </>
        ) : null}
        <dt>Area, net</dt>
        <dd>
          {surface.areaM2.value.toFixed(2)} m²{" "}
          <span className="muted small">this is what the heat loss uses</span>
        </dd>
      </dl>

      {answers.length > 0 ? (
        <form
          className="surface-panel__adjacency"
          aria-label={`What is on the other side of ${surface.name ?? surface.kind}`}
          onSubmit={saveAdjacency}
        >
          {/*
            * A LIST OF ANSWERS, NOT A DROPDOWN.
            *
            * A dropdown hides every option but one, so the question "what is
            * on the other side of this wall" was answered by whatever the
            * scan guessed, by anybody in a hurry. Here all four are on screen,
            * each with an example and what it does to the heat loss, so
            * picking the right one is the easy thing to do rather than the
            * careful thing.
            *
            * Real radio inputs in a real fieldset: a group a keyboard arrows
            * through and a screen reader announces as "1 of 4", which no
            * amount of styled divs with click handlers gives you.
            */}
          <fieldset className="surface-answers">
            <legend>
              {surface.kind === "floor"
                ? "What is under this floor?"
                : "What is on the other side?"}
            </legend>
            {answers.map((option) => {
              const on = answer === option.adjacency;
              return (
                <label
                  key={option.adjacency}
                  className={`surface-answer${on ? " surface-answer--on" : ""}`}
                >
                  <input
                    type="radio"
                    name={answerId}
                    value={option.adjacency}
                    checked={on}
                    disabled={!canEdit || adjacencySubmit.busy}
                    onChange={() => setAnswer(option.adjacency)}
                  />
                  <span className="surface-answer__words">
                    <span className="surface-answer__plain">{option.plain}</span>
                    <span className="surface-answer__hint">{option.hint}</span>
                  </span>
                </label>
              );
            })}
          </fieldset>

          {/* What the chosen answer DOES, before it is saved rather than after. */}
          {chosenAnswer ? (
            <p className="surface-answers__means" data-testid="surface-answer-means">
              {chosenAnswer.means}
            </p>
          ) : null}

          <div className="room-panel__field">
            {surface.adjacencyAssumed === true ? (
              <p className="hint small fail" data-testid="surface-other-side-assumed">
                {surface.kind === "floor"
                  ? "The scan could not tell, so this is being treated as solid and on the ground."
                  : "Nothing was scanned behind it, so the heat loss counts it as outside."}
              </p>
            ) : null}
          </div>

          {needsTemperature ? (
            <div className="room-panel__field">
              <label htmlFor={tempId}>Temperature on the other side, °C</label>
              <input
                id={tempId}
                inputMode="decimal"
                value={temp}
                aria-required="true"
                aria-invalid={touched && "error" in parsedTemp}
                disabled={!canEdit || adjacencySubmit.busy}
                onChange={(event) => setTemp(event.target.value)}
                onBlur={() => setTouched(true)}
              />
              {touched && "error" in parsedTemp ? (
                <p className="hint small fail">{parsedTemp.error}</p>
              ) : null}
              <p className="muted small">{ADJACENCY_TEMPERATURE_SOURCE}</p>
            </div>
          ) : null}

          {canEdit ? (
            <button type="submit" disabled={adjacencySubmit.busy}>
              {adjacencySubmit.busy ? "Saving…" : "Save other side"}
            </button>
          ) : (
            <p className="muted small">
              {adjacencyLabel(surface.adjacency, surface.kind)}
              {surface.adjacencyTempC === null ? "" : ` · ${degrees(surface.adjacencyTempC)}`}
            </p>
          )}

          {surface.adjacencyConfirmed ? (
            <p className="muted small" data-testid="surface-other-side-confirmed">
              Confirmed by a surveyor ({confirmationByline(surface.adjacencyConfirmed)}).
            </p>
          ) : null}
          {adjacencySubmit.error !== null ? (
            <Failure error={adjacencySubmit.error} what="that answer" />
          ) : null}
        </form>
      ) : null}

      <form
        className="surface-panel__construction"
        aria-label={`What ${surface.name ?? surface.kind} is made of`}
        onSubmit={(event) => {
          event.preventDefault();
          if (uInvalid) return;
          const body =
            parsedU !== null
              ? { uValue: parsedU }
              : chosen === ""
                ? {}
                : { constructionId: chosen };
          void constructionSubmit.submit(async () => {
            await client.confirmUValue(surface.id, body);
            onSaved();
          });
        }}
      >
        <div className="room-panel__field">
          <label htmlFor={constructionId}>Construction</label>
          <select
            id={constructionId}
            value={chosen}
            disabled={!canEdit || constructionSubmit.busy || library.length === 0}
            onChange={(event) => {
              setChosen(event.target.value);
              // Picking a construction and typing a U-value are two different
              // answers and the API refuses both at once; choosing one clears
              // the other rather than letting the form send a contradiction.
              setTypedU("");
            }}
          >
            <option value="">
              {library.length === 0
                ? groundFloor
                  ? "No published table for a floor on the ground"
                  : "No published table for this element"
                : "Not set"}
            </option>
            {library.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.label}
              </option>
            ))}
          </select>
          {picked ? (
            <p className="muted small" data-testid="construction-source">
              {formatUValue(picked.uValueWPerM2K)} W/m²K — {picked.source.standard},{" "}
              {picked.source.clause}, p.{picked.source.page}
              {picked.ageBandYears ? ` (age band ${picked.ageBand}, ${picked.ageBandYears})` : ""}.
              {picked.note ? ` ${picked.note}` : ""}
            </p>
          ) : null}
          {groundFloor ? (
            <p className="muted small" data-testid="ground-floor-note">
              {GROUND_FLOOR_NOT_TABULATED}
            </p>
          ) : null}
        </div>

        <div className="room-panel__field">
          <label htmlFor={uValueId}>Or type the U-value you measured, W/m²K</label>
          <input
            id={uValueId}
            inputMode="decimal"
            value={typedU}
            placeholder={String(surface.uValue.value)}
            aria-invalid={uInvalid}
            disabled={!canEdit || constructionSubmit.busy}
            onChange={(event) => {
              setTypedU(event.target.value);
              if (event.target.value.trim() !== "") setChosen("");
            }}
          />
          {uInvalid ? (
            <p className="hint small fail">A U-value has to be a number above zero, up to 10.</p>
          ) : null}
          <p className="muted small" data-testid="u-value-now">
            Now {surface.uValue.value} W/m²K
            {surface.construction ? ` — ${surface.construction}` : ""}
            {surface.uValue.confirmedBySurveyor ? "" : " (nobody has confirmed this)"}
          </p>
        </div>

        {canEdit ? (
          <button type="submit" disabled={constructionSubmit.busy}>
            {constructionSubmit.busy ? "Saving…" : "Save U-value"}
          </button>
        ) : null}
        {constructionSubmit.error !== null ? (
          <Failure error={constructionSubmit.error} what="that U-value" />
        ) : null}
      </form>
    </section>
  );
}
